import { useUserSettingsStore } from '@client/stores/userSettingsStore';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { getTableSettingKey } from '@shared/types/user-settings';

export const LIST_TABLE_PERSIST_KEYS = {
    claims: 'claims-table-columns',
    complaints: 'complaints-table-columns-with-configurable-fields',
    incidents: 'incident-search-columns',
    training: 'training-records-table-columns',
} as const;

/** A fresh prefetch input is exact only after persisted table settings hydrate. */
export function canPrefetchFreshListInput(): boolean {
    return useUserSettingsStore.getState().isInitialized;
}

function readPersistedPageSize(
    tableKey: (typeof LIST_TABLE_PERSIST_KEYS)[keyof typeof LIST_TABLE_PERSIST_KEYS],
): number {
    const settings = useUserSettingsStore.getState();
    if (!canPrefetchFreshListInput()) return DEFAULT_PAGE_SIZE;

    const savedPageSize: unknown = settings.getSetting(getTableSettingKey.pageSize(tableKey));
    return typeof savedPageSize === 'number' && Number.isFinite(savedPageSize) && savedPageSize > 0
        ? Math.max(1, Math.floor(savedPageSize))
        : DEFAULT_PAGE_SIZE;
}

const CLAIMS_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    where: {},
    orderBy: undefined,
    offset: 0,
} satisfies RouterInputs['claim']['listClaims'];

const INCIDENTS_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    orderBy: { id: 'desc' },
    where: {},
    offset: 0,
} satisfies RouterInputs['incident']['listIncidents'];

const COMPLAINTS_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    orderBy: { id: 'desc' },
    where: {},
    offset: 0,
} satisfies RouterInputs['complaint']['listComplaints'];

const TRAINING_BASE_INPUT = {
    limit: DEFAULT_PAGE_SIZE,
    where: { AND: [{ disabled: false }] },
    orderBy: { userId: 'desc' },
    computedFilters: undefined,
    offset: 0,
} satisfies RouterInputs['trainingRecord']['listTrainingRecordsByUser'];

export function freshClaimsListInput(): RouterInputs['claim']['listClaims'] {
    return freshClaimsListInputWithWhere({});
}

export function freshClaimsListInputWithWhere(
    where: RouterInputs['claim']['listClaims']['where'],
): RouterInputs['claim']['listClaims'] {
    return { ...CLAIMS_BASE_INPUT, limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.claims), where };
}

export function freshIncidentsListInput(): RouterInputs['incident']['listIncidents'] {
    return freshIncidentsListInputWithWhere({});
}

export function freshIncidentsListInputWithWhere(
    where: RouterInputs['incident']['listIncidents']['where'],
): RouterInputs['incident']['listIncidents'] {
    return {
        ...INCIDENTS_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.incidents),
        where,
    };
}

export function freshComplaintsListInput(): RouterInputs['complaint']['listComplaints'] {
    return freshComplaintsListInputWithWhere({});
}

export function freshComplaintsListInputWithWhere(
    where: RouterInputs['complaint']['listComplaints']['where'],
): RouterInputs['complaint']['listComplaints'] {
    return {
        ...COMPLAINTS_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.complaints),
        where,
    };
}

export function freshTrainingListInput(): RouterInputs['trainingRecord']['listTrainingRecordsByUser'] {
    return {
        ...TRAINING_BASE_INPUT,
        limit: readPersistedPageSize(LIST_TABLE_PERSIST_KEYS.training),
    };
}

export function applyTrainingFiltersToInput(
    input: RouterInputs['trainingRecord']['listTrainingRecordsByUser'],
    where: NonNullable<RouterInputs['trainingRecord']['listTrainingRecordsByUser']['where']>,
    computedFilters: RouterInputs['trainingRecord']['listTrainingRecordsByUser']['computedFilters'],
): RouterInputs['trainingRecord']['listTrainingRecordsByUser'] {
    return { ...input, where, computedFilters, offset: 0 };
}
